import { Box, IconButton, OsTextField } from "@outschool/backpack";
import { TextMimeType } from "@outschool/gql-frontend-generated";
import { faPaperPlane } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  AttachmentFragmentFragment,
  InlineEditableAttachments,
} from "@outschool/ui-components-classroom";
import {
  AttachmentGallery,
  ErrorMessage,
} from "@outschool/ui-components-shared";
import React, { useCallback, useRef, useState } from "react";

import { useLearnerCommentOnAssignmentMutation } from "../../queries/LearnerClassroomAssignmentsBySectionQueries";
import ConversationTimeline from "./ConversationTimeline";
import { useMessageTeacherDrawerContext } from "./MessageTeacherDrawerContext";
import ThreadViewLayout from "./ThreadViewLayout";
import { FeedbackThreadItem } from "./types";

interface FeedbackThreadDetailViewProps {
  feedbackThread: FeedbackThreadItem;
  onBack: () => void;
  onClose: () => void;
}

export default function FeedbackThreadDetailView({
  feedbackThread,
  onBack,
  onClose,
}: FeedbackThreadDetailViewProps) {
  const { t } = useTranslation(
    "learnerApp\\MessageTeacherButton\\FeedbackThreadDetailView",
  );
  const { section } = useMessageTeacherDrawerContext();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFragmentFragment[]>(
    [],
  );
  const [video, setVideo] = useState<AttachmentFragmentFragment | null>(null);
  const [sendError, setSendError] = useState<unknown>(null);
  const { submitComment, loading } = useLearnerCommentOnAssignmentMutation({
    sectionUid: section.uid,
  });

  const messages = feedbackThread.messages;

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    const hasContent =
      trimmed.length > 0 || attachments.length > 0 || video !== null;
    if (!hasContent || loading) {
      return;
    }
    setSendError(null);
    const sendAttachments = attachments;
    const sendVideo = video;
    setInput("");
    setAttachments([]);
    setVideo(null);
    try {
      const success = await submitComment({
        assignmentClassPostUid: feedbackThread.assignmentClassPostUid,
        messageContentInput: {
          text: trimmed,
          textMimeType: TextMimeType.TextPlain,
          ...(sendAttachments.length > 0
            ? { attachments: sendAttachments }
            : {}),
          ...(sendVideo ? { video: sendVideo } : {}),
        },
      });
      if (!success) {
        setInput(trimmed);
        setAttachments(sendAttachments);
        setVideo(sendVideo);
      }
    } catch (error) {
      setInput(trimmed);
      setAttachments(sendAttachments);
      setVideo(sendVideo);
      setSendError(error);
    }
  }, [
    attachments,
    feedbackThread.assignmentClassPostUid,
    input,
    loading,
    submitComment,
    video,
  ]);

  return (
    <ThreadViewLayout
      postTitle={t("Feedback: {{assignmentTitle}}", {
        assignmentTitle: feedbackThread.assignmentTitle,
      })}
      postSubtitle={undefined}
      onBack={onBack}
      onClose={onClose}
    >
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          overflowY: "auto",
          p: 12,
        }}
      >
        <ConversationTimeline
          messages={messages}
          conversationKey={feedbackThread.uid}
          scrollContainerRef={scrollContainerRef}
        />
      </Box>

      <Box sx={{ borderTop: "1px solid", borderColor: "divider" }}>
        <ErrorMessage value={sendError} sx={{ marginBottom: 0 }} />
        {(attachments.length > 0 || video) && (
          <Box sx={{ padding: "1.2rem 1.6rem 0" }}>
            <AttachmentGallery
              video={video}
              attachments={attachments}
              onDelete={attachment => {
                if (video?.uid === attachment.uid) {
                  setVideo(null);
                  return;
                }
                setAttachments(prev =>
                  prev.filter(a => a.uid !== attachment.uid),
                );
              }}
              containerSx={{ marginBottom: 0 }}
              primaryAttachmentMaxHeight="120px"
            />
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: "0.8rem",
            padding: "0.75rem 1.2rem",
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <InlineEditableAttachments
              showVideoUpload={true}
              video={video}
              value={attachments}
              onChange={setAttachments}
              onVideoChanged={setVideo}
              sx={{ flexShrink: 0, color: "text.secondary" }}
            />
          </Box>
          <OsTextField
            ariaLabel={t("Write a reply...")}
            size="xsmall"
            fullWidth
            multiline
            maxRows={4}
            placeholder={t("Write a reply...")}
            value={input}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInput(e.target.value)
            }
            disabled={loading}
          />
          <IconButton
            icon={faPaperPlane}
            aria-label={t("Send message")}
            disabled={
              loading || (!input.trim() && attachments.length === 0 && !video)
            }
            onClick={handleSend}
            sx={{
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { backgroundColor: "primary.dark" },
              "&.Mui-disabled": {
                backgroundColor: "action.disabledBackground",
                color: "action.disabled",
              },
              width: 40,
              height: 40,
              flexShrink: 0,
            }}
          />
        </Box>
      </Box>
    </ThreadViewLayout>
  );
}
